import { useState } from 'react';

import { useT } from '@shared/i18n';
import { useIsOnline } from '@shared/lib/hooks';
import { formatMegabytes } from '@shared/lib/format-bytes';
import { useClearAppData } from '@features/clear-app-data';
import {
	useStorageBreakdown,
	type StorageBreakdown,
	type StorageGroup,
} from '@features/storage-breakdown';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogTitle,
} from '@shared/ui/dialog';

import type { SettingControlProps } from './setting-controls';
import styles from './settings-dialog.module.css';

/** What Clear cache removes; the App and Other groups stay. */
const CLEARED_BY_CACHE: ReadonlyArray<StorageGroup> = [
	'downloads',
	'images',
	'video',
	'audio',
	'content',
];

/**
 * Settings → Offline & storage → Cache: two levels, after iOS's Reset and
 * Erase All Content and Settings. Both need a connection (the app reloads
 * and fetches again) and both confirm first, saying what goes, what stays
 * and that the app reloads.
 */
export function ClearCachePanel({ labelId }: SettingControlProps) {
	const t = useT();
	const { clearCache } = useClearAppData();
	return (
		<ConfirmedAction
			labelId={labelId}
			button={t('settings.clearCache.label')}
			title={t('settings.clearCache.confirmTitle')}
			body={[t('settings.clearCache.confirmBody'), t('settings.clearCache.confirmKeeps')]}
			confirm={t('settings.clearCache.label')}
			freed={(breakdown) =>
				CLEARED_BY_CACHE.reduce((sum, group) => sum + breakdown.groups[group], 0)
			}
			run={clearCache}
		/>
	);
}

export function EraseAllPanel({ labelId }: SettingControlProps) {
	const t = useT();
	const { eraseAllData } = useClearAppData();
	return (
		<ConfirmedAction
			labelId={labelId}
			danger
			button={t('settings.eraseAll.button')}
			title={t('settings.eraseAll.confirmTitle')}
			body={[t('settings.eraseAll.confirmBody')]}
			confirm={t('settings.eraseAll.button')}
			freed={(breakdown) => breakdown.total}
			run={eraseAllData}
		/>
	);
}

function ConfirmedAction({
	labelId,
	danger,
	button,
	title,
	body,
	confirm,
	freed,
	run,
}: {
	labelId?: string;
	danger?: boolean;
	button: string;
	title: string;
	body: ReadonlyArray<string>;
	confirm: string;
	freed: (breakdown: StorageBreakdown) => number;
	run: () => Promise<void>;
}) {
	const t = useT();
	const isOnline = useIsOnline();
	const [open, setOpen] = useState(false);
	const [running, setRunning] = useState(false);

	return (
		<div className={styles['actions']}>
			{isOnline ? null : (
				<span className={styles['value']}>{t('settings.cache.offline')}</span>
			)}
			<button
				type="button"
				className={styles['action']}
				data-danger={danger || undefined}
				disabled={!isOnline}
				onClick={() => setOpen(true)}
				aria-describedby={labelId}
			>
				{button}
			</button>
			<Dialog open={open} onOpenChange={(next) => !running && setOpen(next)}>
				<DialogContent
					showCloseButton={false}
					className={styles['confirm']}
					backdropClassName={styles['confirm-backdrop']}
				>
					<DialogTitle className={styles['confirm-title']}>{title}</DialogTitle>
					<DialogDescription render={<div />} className={styles['confirm-body']}>
						{open ? <FreedSpace freed={freed} /> : null}
						{body.map((paragraph) => (
							<p key={paragraph}>{paragraph}</p>
						))}
						<p>
							<strong>{t('settings.cache.reloads')}</strong>
						</p>
					</DialogDescription>
					<div className={styles['actions']}>
						<button
							type="button"
							className={styles['action']}
							disabled={running}
							onClick={() => setOpen(false)}
						>
							{t('common.cancel')}
						</button>
						<button
							type="button"
							className={styles['action']}
							data-danger
							disabled={running || !isOnline}
							onClick={() => {
								setRunning(true);
								void run().catch(() => setRunning(false));
							}}
						>
							{confirm}
						</button>
					</div>
				</DialogContent>
			</Dialog>
		</div>
	);
}

/** "Frees about X MB", measured when the dialog opens. */
function FreedSpace({ freed }: { freed: (breakdown: StorageBreakdown) => number }) {
	const t = useT();
	const { breakdown } = useStorageBreakdown();
	if (!breakdown) {
		return null;
	}
	return (
		<p>{t('settings.cache.frees', { size: formatMegabytes(freed(breakdown)) })}</p>
	);
}
